// Szerepkör-jelölő ikonok — a szöveges szerep-badge iparági, letisztult utódja. Elv: A KIVÉTELT emeld ki,
// ne a normát → CSAK az EMELT szerepkörökhöz (superadmin/admin) tartozik ikon; a „Tag" (member) és minden más
// szerep ikon NÉLKÜL marad (zajmentes lista). MONOKRÓM shield-tier (Linear/Vercel-esztétika, iparági best
// practice): egyetlen forma (pajzs = privilégium), a TIER a kitöltés + tónus — Szuperadmin = TÖMÖR pajzs
// (`text-foreground`), Adminisztrátor = VONALAS pajzs (`text-muted-foreground`). A SZÍN szándékosan a
// STÁTUSZNAK van fenntartva (aktív/felfüggesztett) — a szerepkör diszkrét, monokróm jel, nem verseng a
// státusz-színnel. A megkülönböztetés NEM csak szín (WCAG/színvak-barát): tömör vs. vonalas FORMA + tónus. A
// teljes szerepkör-nevet a globális tooltip + `aria-label` adja. Újrahasznosítható (jogosultságok/munkatársak).
import {
  ShieldCheckIcon as ShieldCheckOutline,
  UserIcon as UserOutline,
} from '@heroicons/react/24/outline';
import { ShieldCheckIcon as ShieldCheckSolid } from '@heroicons/react/24/solid';
import type { ComponentType, SVGProps } from 'react';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { roleLabel } from '@/lib/permission-catalog';
import { cn } from '@/lib/utils';

interface RoleVisual {
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** Szemantikus tónus-token (nem nyers hex): a design-rendszer `text-*` utilityje. Monokróm — nincs hue. */
  className: string;
}

// CSAK az emelt szerepkörök kapnak ikont; a `member` (Tag) + minden más szándékosan hiányzik → nincs ikon.
// Tömör pajzs + teli tónus = legfelső (Szuperadmin); vonalas pajzs + halvány tónus = második (Admin).
// A PARTNER-létra ugyanezt az elvet követi, de SAJÁT jelekkel — mert ott a két emelt szerep NEM ugyanaz a
// dolog kétféle erősségben: a Portál-adminisztrátor a PRIVILÉGIUMÁVAL emelkedik ki (ő oszthat hozzáférést a
// fiókján) → pajzs, mint a belső Adminisztrátornál; a Portál-felhasználó viszont nem privilégiumban, hanem
// RÉSZVÉTELBEN több a megtekintőnél (jegyet nyit, válaszol, visszajelez) → személy-ikon. A Portál-megtekintő
// (csak olvas) ikon nélkül marad, mint belül a Tag: a kivételt jelöljük, nem a normát.
const ELEVATED: Record<string, RoleVisual> = {
  superadmin: { Icon: ShieldCheckSolid, className: 'text-foreground' },
  admin: { Icon: ShieldCheckOutline, className: 'text-muted-foreground' },
  'portal-admin': { Icon: ShieldCheckOutline, className: 'text-muted-foreground' },
  'portal-member': { Icon: UserOutline, className: 'text-muted-foreground' },
};

export interface RoleIconsProps {
  /** A tag nyers szerep-kulcsai (a `/employees` BFF-ből). Csak az emeltekhez rajzolunk ikont. */
  roles: readonly string[];
  className?: string;
}

/** Az emelt szerepekhez tartozó ikonok tooltippel; ha nincs emelt szerep, semmit sem renderel (`null`). */
export function RoleIcons({ roles, className }: RoleIconsProps): JSX.Element | null {
  const elevated = roles.filter((r) => r in ELEVATED);
  if (elevated.length === 0) return null;
  return (
    <span className={cn('inline-flex items-center gap-1', className)}>
      {elevated.map((role) => {
        const { Icon, className: color } = ELEVATED[role]!;
        const label = roleLabel(role);
        return (
          <Tooltip key={role}>
            <TooltipTrigger asChild>
              {/* Fókuszálható span (billentyűzet-tooltip); nem akció → nem gomb. A Radix köti az aria-describedby-t. */}
              <span
                tabIndex={0}
                role="img"
                aria-label={label}
                className="focus-visible:ring-ring inline-flex cursor-default rounded-sm outline-none focus-visible:ring-2"
              >
                <Icon aria-hidden className={cn('size-4 shrink-0', color)} />
              </span>
            </TooltipTrigger>
            <TooltipContent>{label}</TooltipContent>
          </Tooltip>
        );
      })}
    </span>
  );
}
